import { AttachedFiles } from "legacyComponents/FileUploadContainer.types";
import { type Moment } from "moment";
import moment from "moment";
import { type RouteComponentProps } from "react-router";

import { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";

import { type CostTypes } from "types/enum";

import { calculateBuilderCost } from "utilities/lineItem/lineItemHelper";
import { recalculateAllFields } from "utilities/lineItem/markupUtility";
import { type IHasEmptyStateBannerProps } from "utilities/list/list.types";

import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { type DismissButtonFormActions } from "commonComponents/entity/emptyState/common/DismissButton/DismissButton";
import { type IGridSettingsHandler } from "commonComponents/utilities/Grid/common/GridSettings/GridSettings.api.handler";
import { type ICostLineItem } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { type ICostCatalogHandler } from "entity/costCatalog/CostCatalog.api.handler";
import { type default as CostGroupsForImportExample } from "entity/costCatalog/CostGroupImport.api.json";
import { type CostItemListFormActions } from "entity/costCatalog/CostItemList/CostItemList.api.types";
import { type ICostItemLineItem } from "entity/CostGroup/CostGroup.api.types";
import { type ICostItemEntity } from "entity/costItem/CostItem/CostItem.api.types";
import { type IFilterHandler } from "entity/filters/Filter/Filter.api.handler";
import { type IExternalProductLink, type VendorTypes } from "entity/vendor/common/vendor.types";

import { type default as CostItemsImportAPIResponseExample } from "./CostItemImport.api.json";
import { type ICostItemListHandler } from "./CostItemList/CostItemList.api.handler";

export type CostCatalogFormActions = DismissButtonFormActions | undefined;

export enum CostCatalogViewType {
    Setup = 1,
    LineItemContainer = 2,
    CostGroupEditor = 3,
    // VendorCatalog = 4,
    Proposal = 5,
    FormatTab = 6,
    EstimateList = 7,
    Rebates = 8,
    Selections = 9,
}

export enum RecursiveUpdateTypes {
    DoNotUpdate = 0,
    SoftUpdate = 1,
    Update = 2,
}

/**
 * Runs `proceed` once any unsaved work in the active catalog tab has been resolved. If the tab is
 * clean, `proceed` runs immediately; if it is dirty, the tab prompts and only runs `proceed` after
 * the user saves or discards. Dismissing the prompt never calls `proceed`.
 */
export type CatalogCloseGuard = (proceed: () => void) => void;

export interface ICatalogCloseGuardRegistration {
    /**
     * Hands the Catalog host a guard to run before the modal closes. Optional because the catalog
     * tabs also render on the standalone (non-modal) catalog page, where there is no close to guard.
     * Tabs register while active and pass `null` on cleanup.
     */
    registerCloseGuard?: (guard: CatalogCloseGuard | null) => void;
}

export interface ICostCatalogProps extends RouteComponentProps, IHasEmptyStateBannerProps {
    viewType?: CostCatalogViewType;
    defaultTab?: CostCatalogTabs;
    availableTabs?: CostCatalogTabs[];
    costCatalogHandler?: ICostCatalogHandler;
    onAddCostItemLineItems?: (addFromCatalogRequest: IAddFromCatalogRequest) => Promise<void>;
    onAddSelectionsFromCatalog?: (payload: {
        optionIds: number[];
        groupIds: number[];
    }) => Promise<void>;
    /**
     * Invoked with the selected catalog spec ids when the user confirms an import from the Specs
     * tab. Providing this also opts the Specifications tab into the import context (see CostCatalog's
     * showSpecificationsCatalogTab gate). The parent owns the API call + page refresh.
     */
    onAddSpecificationsFromCatalog?: (specificationIds: number[]) => Promise<void>;
    canAddAllowances?: boolean;
    addToEntityDisplayName?: string;
    modalConfig?: IModalConfiguration;
    inactivationCutoffDate?: Moment;
    onUpdateParentLineItems?: (
        isBreakingCostItemLinks?: boolean | null,
        costItem?: ICostItemEntity,
    ) => void;
    jobIds?: number[];
    listHandler?: ICostItemListHandler;
    filterHandler?: IFilterHandler;
    gridSettingsHandler?: IGridSettingsHandler;
    lineItemToReplace?: ICostLineItem;
    lockCostCode?: boolean;
    hasSingleSelectCostTypes?: boolean;
}

export class CostCatalogSettings {
    constructor(data: any) {
        this.canAddCostGroups = data.canAddCostGroups;
        this.canAddCostItems = data.canAddCostItems;
        this.canAddFromCatalog = data.canAddFromCatalog;
        this.hasCostItemImport = data.hasCostItemImport;
        this.showCostGroups = data.showCostGroups;
        this.showCostItems = data.showCostItems;
        this.vendorCatalogInformation =
            data.vendorCatalogInformation &&
            new VendorCatalogInformation(data.vendorCatalogInformation);
        this.hasCostCodesFeature = data.hasCostCodesFeature;
    }
    canAddCostGroups: boolean;
    canAddCostItems: boolean;
    canAddFromCatalog: boolean;
    hasCostItemImport: boolean;
    showCostGroups: boolean;
    showCostItems: boolean;
    vendorCatalogInformation: VendorCatalogInformation | null;
    hasCostCodesFeature: boolean;
}

export class VendorCatalogInformation {
    constructor(data: any) {
        this.homeDepotCatalogInformation =
            data.homeDepotCatalogInformation &&
            new HomeDepotCatalogInformation(data.homeDepotCatalogInformation);
        this.isNewToHomeDepotCatalogSync = data.isNewToHomeDepotCatalogSync;
        this.isHomeDepotCompatibleBuilderCountry = data.isHomeDepotCompatibleBuilderCountry;
    }

    homeDepotCatalogInformation: HomeDepotCatalogInformation | null;
    isNewToHomeDepotCatalogSync: boolean;
    isHomeDepotCompatibleBuilderCountry: boolean;
}

export class HomeDepotCatalogInformation {
    constructor(data: any) {
        this.isConnectedToHomeDepot = data.isConnectedToHomeDepot;
        this.lastHomeDepotSyncDate = data.lastHomeDepotSyncDate
            ? moment(data.lastHomeDepotSyncDate)
            : null;
        this.homeDepotStoreName = data.homeDepotStoreName;
        this.newHomeDepotProductCount = data.newHomeDepotProductCount;
    }

    isConnectedToHomeDepot: boolean;
    lastHomeDepotSyncDate: Moment | null;
    homeDepotStoreName: string;
    newHomeDepotProductCount: number;
}

export interface ICostCatalogState {
    settings: CostCatalogSettings;
    currentCostItemImportType: VendorTypes | null;
    actionBeingPerformed: CostCatalogFormActions;
    isHomeDepotUnconnectedBannerDismissed: boolean;
    // These props are passed to each tab to inform them when the component updates if they should refresh data based on changes in the other tab.
    shouldRefreshCostItems: boolean;
    shouldRefreshCostGroups: boolean;
}

export enum CostCatalogTabs {
    CostItems = "costItems",
    CostGroups = "costGroups",
    SelectionsCatalog = "selectionsCatalog",
    Specifications = "specifications",
}

export interface IAddFromCatalogRequest {
    lineItems: ICostItemLineItem[];
    formatParentId?: number;
    costGroups?: IAddCostGroup[];
    includeDescription?: boolean;
    includeFiles?: boolean;
    includeSpecs?: boolean;
}

export interface IAddCostGroup {
    costItemCount: number;
    costGroupId: number;
    description: string;
    title: string;
    files: AttachedFiles;
}
export interface ICostCatalogTitleHeaderProps extends RouteComponentProps {
    currentTab: CostCatalogTabs;
    settings: CostCatalogSettings;
    viewType: CostCatalogViewType;
    onAddGridLineItems?: () => Promise<void>;
    addToEntityDisplayName?: string;
    actionBeingPerformed?: CostItemListFormActions;
    onFilterClick?: () => void;
    filterCount?: number;
    onExportClick?: () => void;
    showExport?: boolean;
}

export interface ICostItemTitleHeaderProps extends ICostCatalogTitleHeaderProps {
    onImportCostItems: (vendorType: VendorTypes) => void;
    lineItemToReplace?: ICostLineItem;
    isSmallScreen?: boolean;
}

export interface ICostGroupTitleHeaderProps extends ICostCatalogTitleHeaderProps {
    areItemsChecked: boolean;
    checkedActions?: React.ReactNode;
    onCopyToCostGroup: () => Promise<void>;
    onAddNewCostGroup: () => void;
}

export class PendingUpdatesResponse {
    constructor(data: any) {
        this.pendingCostItemUpdates = data.pendingCostItemUpdates;
        this.hasPendingUpdates = data.hasPendingUpdates;
        this.costItemUpdateMessage = data.costItemUpdateMessage;
    }

    pendingCostItemUpdates: number;
    hasPendingUpdates: boolean;
    costItemUpdateMessage: string | null;
}

export class LineItemsBySelectionResponse {
    constructor(data: any) {
        this.lineItemIds = data.lineItemIds;
    }

    lineItemIds: number[];
}

export class CostItemsForImportResponse {
    constructor(data: typeof CostItemsImportAPIResponseExample) {
        this.lineItems = data.costItems.map((ci) => {
            const lineItem = {
                costCodeId: ci.CostCodeId,
                description: ci.Description,
                costItemId: ci.Id,
                markupPerUnit: ci.MarkupPerUnit ?? 0,
                markupPercent: ci.MarkupPercentage ?? 0,
                margin: ci.Margin ?? 0,
                internalNotes: ci.Notes,
                ownerPrice: ci.OwnerPrice ?? 0,
                itemTitle: ci.ProductTitle,
                quantity: ci.Quantity,
                quantityIsLinked: ci.QuantityIsLinked,
                unit: ci.UnitType,
                unitCost: ci.unitCost,
                markupAmount: ci.MarkupPrice ?? 0,
                markupType: (ci.MarkupColumn ?? MarkupColumnTypes.Percentage) as MarkupColumnTypes,
                markupIsLinked: ci.MarkupColumn !== undefined && ci.MarkupColumn !== null,
                id: 0,
                builderCost: calculateBuilderCost(ci.unitCost, ci.Quantity),
                costTypes: ci.CostTypes,
                markedAs: ci.MarkedAs,
                costCodeTitle: "",
                costGroupQuantity: 1,
                costCategoryId: ci.CostCategoryId,
                vendorProductId: ci.VendorProductId ?? undefined,
                vendorType: ci.VendorType as VendorTypes,
                isDiscontinued: ci.IsDiscontinued,
                productLink: ci.ProductLink
                    ? ({
                          label: ci.ProductLink.label,
                          value: ci.ProductLink.value,
                          helpText: ci.ProductLink.helpText,
                      } as IExternalProductLink)
                    : undefined,
            };

            const calculatedFields = recalculateAllFields(lineItem);
            return {
                ...lineItem,
                ...calculatedFields,
            };
        });
        this.includeFiles = data.includeFiles;
    }

    lineItems: ICostItemLineItem[];
    includeFiles: boolean;
}

export class CostGroupsForImportResponse {
    constructor(data: typeof CostGroupsForImportExample) {
        const lineItems: ICopyCostGroupLineItem[] = data.costItems.map((ci) => ({
            builderCost: calculateBuilderCost(ci.unitCost, ci.Quantity),
            costCodeId: ci.CostCodeId,
            costItemId: ci.Id,
            costTypes: ci.CostTypes,
            markedAs: ci.MarkedAs,
            description: ci.Description,
            id: 0,
            internalNotes: ci.Notes,
            itemTitle: ci.ProductTitle,
            costCodeTitle: ci.CostCodeTitle,
            markupAmount: ci.MarkupPrice ?? 0,
            costGroupQuantity: 0,
            markupPerUnit: ci.MarkupPerUnit ?? 0,
            markupPercent: ci.MarkupPercentage ?? 0,
            markupType: (ci.MarkupColumn ?? MarkupColumnTypes.Percentage) as MarkupColumnTypes,
            markupIsLinked: ci.MarkupColumn !== undefined && ci.MarkupColumn !== null,
            margin: ci.Margin ?? 0,
            ownerPrice: ci.OwnerPrice ?? 0,
            quantityIsLinked: ci.QuantityIsLinked,
            unitCost: ci.unitCost,
            quantity: ci.Quantity,
            unit: ci.UnitType,
            parentId: ci.ParentId,
            isSelected: true,
            proposalFormatItemId: ci.ProposalFormatItemId,
            costCategoryId: ci.CostCategoryId,
            vendorProductId: ci.VendorProductId,
            vendorType: ci.VendorType as VendorTypes,
            isDiscontinued: ci.IsDiscontinued,
            productLink: ci.ProductLink
                ? ({
                      label: ci.ProductLink.label,
                      value: ci.ProductLink.value,
                      helpText: ci.ProductLink.helpText,
                  } as IExternalProductLink)
                : undefined,
        }));
        this.costGroups = data.costGroups.map((cg) => {
            return {
                title: cg.title,
                id: cg.id,
                description: cg.description,
                lineItems: lineItems.filter((ci) => ci.parentId === cg.id),
                files: new AttachedFiles(cg.files),
            };
        });
        this.inactiveItems = data.inactiveItems;
        this.builderFlatRateId = data.builderFlatRateId;
    }

    costGroups: ICostGroupForImport[];
    inactiveItems: IInactiveLineItem[];
    builderFlatRateId: number;
}

export interface ICostGroupForImport {
    title: string;
    id: number;
    description: string;
    files: AttachedFiles;
    lineItems?: ICopyCostGroupLineItem[];
}

export interface ICopyCostGroupLineItem extends ICostItemLineItem {
    isSelected: boolean;
    proposalFormatItemId: number;
    costTypes: CostTypes[] | null;
}

export interface IInactiveLineItem {
    id: number;
    title: string;
    count: number;
}
